import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { BehaviorSubject, Observable, ReplaySubject } from "rxjs";
import { Autor } from "@src/app/models/interfaces/autor.interface";
import { GlobalConstants } from "../../global-constants";
import { PlatformService } from "../platform/platform.service";

@Injectable()
export class NoticiasService {
  private readonly API_URL: string;
  private readonly API_SHARED_URL: string;
  private readonly constantesGlobales: any;
  private readonly isCartaEditorial = new BehaviorSubject<boolean | null>(null);
  public isCartaEditorial$: Observable<boolean> = this.isCartaEditorial.asObservable();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly platformService: PlatformService,
  ) {
    const host = this.platformService.getLocationHostname();
    this.constantesGlobales = new GlobalConstants(host);
    this.API_URL = this.constantesGlobales.urlApi;
    this.API_SHARED_URL = this.constantesGlobales.urlSharedApi;
  }

  /**
   * Obtiene todas las noticias de la hemeroteca aportando el número de página, el tamaño de la página y el id del ámbito.
   * @param {number} currentPage - Número de página.
   * @param {number} pageSize - Tamaño de la página.
   * @param {number} idAmbito - Id del ámbito.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getAllNews(currentPage: number, pageSize: number, idAmbito: number) {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = new HttpParams().set("limit_videos", pageSize).set("id_ambito", idAmbito).set("current_page", currentPage);
    return this.httpClient.get(this.API_URL + "/getHemeroteca", { headers: headers, params: body });
  }

  /**
   * Obtiene las últimas noticias aportando el límite de vídeos, el filtro y la búsqueda.
   * @param {number} limit_videos - Número limite máximo de vídeos.
   * @param {string} filter_by - Filtro por el que se busca.
   * @param {string} search_by - Búsqueda por la que se busca.
   * @param {number} idAmbito - Id del ámbito.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */

  public getUltimasNoticias(limit_videos: number, filter_by: string, search_by: string, idAmbito: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    if (!limit_videos) {
      limit_videos = 45;
    }
    let body = new HttpParams().set("limit_videos", String(limit_videos)).set("id_ambito", String(idAmbito));
    if (filter_by) {
      body = new HttpParams().set("limit_videos", String(limit_videos)).set("id_ambito", String(idAmbito)).set("filter_by", filter_by).set("search_by", search_by);
    }
    return this.httpClient.get(this.API_URL + "/ultimosVideos", { headers: headers, params: body });
  }

  // Un subject que emite el total de noticias para la hemeroteca
  private totalNoticias$: ReplaySubject<any> = new ReplaySubject(1);

  /**
   * Devuelve el total de noticias como observable.
   * @returns {Observable<any>} - Observable con el total de noticias.
   */
  public getTotalUltimasNoticias(): Observable<any> {
    return this.totalNoticias$.asObservable();
  }
  /**
   * Define el total de noticias.
   * @param value - Asigna el valor del total de noticias.
   */
  public setTotalUltimasNoticias(value) {
    this.totalNoticias$.next(value);
  }

  /**
   * Obtiene las noticias fijadas aportando el id del ámbito.
   * @param {number} idAmbito - Id del ámbito.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getNoticiasFijadas(idAmbito: number, page: number = 1, page_limit: number = 5): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });

    const body = new HttpParams().set("id_ambito", String(idAmbito)).set("page_number", String(page)).set("page_limit", String(page_limit));
    return this.httpClient.get<any>(this.API_SHARED_URL + "/noticiasFijas", { headers: headers, params: body });
  }

  /**
   * Obtiene las noticias de una sección aportando el id de la sección y el id del ámbito.
   * @param limitVideos - Número de vídeos máximo a obtener.
   * @param idAmbito - Id del ámbito.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getVideosMasVistosApi(limitVideos: number, idAmbito: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    if (!limitVideos) {
      limitVideos = 10;
    }
    const body = new HttpParams().set("limit_videos", String(limitVideos)).set("idAmbito", String(idAmbito));
    return this.httpClient.get(this.API_URL + "/videosMasVistos", { headers: headers, params: body });
  }

  // Un subject que emite las noticias mas vistas
  private vidoesMasVistos$: ReplaySubject<any> = new ReplaySubject(1);
  public getVideosMasVistos(): Observable<any> {
    return this.vidoesMasVistos$.asObservable();
  }
  public setVideosMasVistos(value) {
    this.vidoesMasVistos$.next(value);
  }

  public getNoticiasSeccion(idSeccion: number, numeroVideos: number, searchBy: string, where: any): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    if (!numeroVideos) {
      numeroVideos = 1;
    }
    const body = new HttpParams().set("id_seccion", String(idSeccion)).set("limit_videos", String(numeroVideos)).set("search_by", searchBy).set("where", where);
    return this.httpClient.get(this.API_URL + "/getNoticiasSeccion", { headers: headers, params: body });
    // return ;
  }

  /**
   * Obtiene la información de un autor basado en su ID de usuario.
   *
   * @param id - El identificador único del usuario.
   * @returns Un observable que emite un objeto de tipo `Autor` con la información del autor.
   */
  public getAutorByUserId(id: string): Observable<Autor> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    return this.httpClient.get<Autor>(`${this.API_URL}/getAutorByUserId/${id}`, { headers: headers });
  }

  /**
   * Obtiene la estructura de noticias por sección según el ID del ámbito proporcionado.
   *
   * @param idAmbito - El identificador numérico del ámbito para el cual se desea obtener la estructura de noticias.
   * @returns Un observable que emite la respuesta del servidor con la estructura de noticias.
   */
  public getEstructuraNoticiasBySeccion(idAmbito: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = new HttpParams().set("id_ambito", String(idAmbito));
    return this.httpClient.get<any>(this.API_URL + "/estructuraNoticiasBySeccion", { headers: headers, params: body });
  }
}
